import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ApiClient, ApiError } from "../api/client";

/** Tenant devuelto por /internal/tenants (solo id + name necesitamos). */
interface TenantSummary {
  id: string;
  name: string;
}

/**
 * Sesión del panel.
 *
 * Flujo real de la API (ver `apps/api/src/routes/auth.ts`):
 *  1. `POST /auth/login` → token **sin** tenant si el usuario pertenece a
 *     varios o es super-admin, y la lista de tenants disponibles.
 *  2. `POST /auth/select-tenant` → token con `tenantId` y rol ya verificados
 *     contra `UserTenant` en BD.
 *  3. `POST /auth/switch-tenant` para cambiar de tenant activo (el
 *     super-admin puede operar "como" cualquiera).
 *
 * Punto que no se puede relajar: el `tenantId` **nunca** se manda en el
 * cuerpo de las peticiones de negocio. Va dentro del token, que es lo único
 * que el servidor acepta como fuente de autorización. `switch-tenant` es la
 * única excepción y existe precisamente para emitir ese token.
 */

const TOKEN_STORAGE_KEY = "deca.session";

/** Persiste la sesión en sessionStorage de forma síncrona. */
function persistSession(session: SessionState): void {
  try {
    if (!session.token) sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    else sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* sin persistencia; el estado en memoria sigue funcionando */
  }
}

/** Segundo factor del acceso: código de la app (o de recuperación) o PIN de conductor. */
export interface SecondFactorInput {
  mfaCode?: string;
  pin?: string;
}

export interface SessionTenant {
  tenantId: string;
  tenantName: string;
  role: string;
}

export interface SessionState {
  token: string | null;
  tenants: SessionTenant[];
  activeTenantId: string | null;
  requiresTenantSelection: boolean;
}

interface AuthContextValue extends SessionState {
  isAuthenticated: boolean;
  /** `true` si hay token pero todavía no hay tenant activo. */
  needsTenant: boolean;
  login: (email: string, password: string, secondFactor?: SecondFactorInput) => Promise<void>;
  selectTenant: (tenantId: string) => Promise<void>;
  /** Cambia de tenant cuando ya hay uno activo (usa switch-tenant). */
  switchTenant: (tenantId: string) => Promise<void>;
  /** Solo superAdmin: vuelve a modo plataforma (sin tenant activo). */
  deselectTenant: () => Promise<void>;
  logout: () => void;
  apiError: ApiError | null;
  clearApiError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface StoredSession {
  token: string;
  tenants: SessionTenant[];
  activeTenantId: string | null;
  requiresTenantSelection: boolean;
}

function readStoredSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    return typeof parsed.token === "string" ? parsed : null;
  } catch {
    return null;
  }
}

export interface AuthProviderProps {
  children: ReactNode;
  /**
   * Cliente sin token, para las llamadas de login: así el proveedor no
   * depende del cliente autenticado y se evita una dependencia circular.
   */
  baseUrl?: string;
}

export function AuthProvider({ children, baseUrl }: AuthProviderProps) {
  const [state, setState] = useState<SessionState>(() => {
    const stored = readStoredSession();
    return {
      token: stored?.token ?? null,
      tenants: stored?.tenants ?? [],
      activeTenantId: stored?.activeTenantId ?? null,
      requiresTenantSelection: stored?.requiresTenantSelection ?? false,
    };
  });
  const [apiError, setApiError] = useState<ApiError | null>(null);

  // sessionStorage se persiste sincrónicamente dentro de cada función que
  // modifica el estado (login, selectTenant, switchTenant, deselectTenant,
  // logout). NO usamos useEffect para esto: el effect es asíncrono y puede
  // ejecutarse DESPUÉS de que navigate() recargue el árbol de rutas, con lo
  // que la lectura inicial de readStoredSession() encontraría el estado viejo.

  const anonymousClient = useMemo(
    () => new ApiClient({ baseUrl: baseUrl ?? "", getToken: () => null }),
    [baseUrl],
  );

  const login = useCallback(
    async (email: string, password: string, secondFactor?: SecondFactorInput) => {
      setApiError(null);
      try {
        const result = await anonymousClient.post<{
          accessToken: string;
          requiresTenantSelection: boolean;
          availableTenants: SessionTenant[];
        }>("/api/v1/auth/login", {
          email,
          password,
          ...(secondFactor?.mfaCode ? { mfaCode: secondFactor.mfaCode } : {}),
          ...(secondFactor?.pin ? { pin: secondFactor.pin } : {}),
        });

        // Si el login devuelve requiresTenantSelection=true y la lista de
        // tenants del usuario está vacía (superAdmin puro sin memberships),
        // cargamos la lista completa de tenants desde /internal/tenants con el
        // token de plataforma. Esta lista se guarda en state.tenants y persiste
        // en sessionStorage; selectTenant/switchTenant/deselectTenant la
        // preservan con el spread ...previous.
        let tenants: SessionTenant[] = result.availableTenants;
        if (result.requiresTenantSelection && result.availableTenants.length === 0) {
          try {
            const platformClient = new ApiClient({
              baseUrl: baseUrl ?? "",
              getToken: () => result.accessToken,
            });
            const tenantsResult = await platformClient.get<{
              items: TenantSummary[];
              canCrossTenants: boolean;
            }>("/api/v1/internal/tenants", {});
            tenants = tenantsResult.items.map((t) => ({
              tenantId: t.id,
              tenantName: t.name,
              role: "ADMIN",
            }));
          } catch {
            // Si falla la carga de tenants no bloqueamos el login; el
            // selector quedará vacío pero el usuario podrá acceder en modo
            // plataforma igualmente.
            tenants = [];
          }
        }

        const newSession: SessionState = {
          token: result.accessToken,
          tenants,
          // Con un único tenant y sin super-admin, la API ya devuelve el token
          // con tenant: no hay nada que elegir.
          activeTenantId: result.requiresTenantSelection
            ? null
            : result.availableTenants[0]?.tenantId ?? null,
          requiresTenantSelection: result.requiresTenantSelection,
        };
        persistSession(newSession);
        setState(newSession);
      } catch (error) {
        if (error instanceof ApiError) setApiError(error);
        throw error;
      }
    },
    [anonymousClient, baseUrl],
  );

  const selectTenant = useCallback(
    async (tenantId: string) => {
      if (!state.token) throw new Error("No hay sesión iniciada");
      setApiError(null);
      const client = new ApiClient({ baseUrl: baseUrl ?? "", getToken: () => state.token });
      const result = await client.post<{ accessToken: string; tenantId: string; role: string }>(
        "/api/v1/auth/select-tenant",
        { tenantId },
      );
      setState((previous) => {
        const next = { ...previous, token: result.accessToken, activeTenantId: result.tenantId, requiresTenantSelection: false };
        persistSession(next);
        return next;
      });
    },
    [baseUrl, state.token],
  );

  /** Cambia de tenant cuando ya hay uno activo → usa switch-tenant. */
  const switchTenant = useCallback(
    async (tenantId: string) => {
      if (!state.token) throw new Error("No hay sesión iniciada");
      setApiError(null);
      const client = new ApiClient({ baseUrl: baseUrl ?? "", getToken: () => state.token });
      const result = await client.post<{ accessToken: string; tenantId: string; role: string }>(
        "/api/v1/auth/switch-tenant",
        { tenantId },
      );
      setState((previous) => {
        const next = { ...previous, token: result.accessToken, activeTenantId: result.tenantId, requiresTenantSelection: false };
        persistSession(next);
        return next;
      });
    },
    [baseUrl, state.token],
  );

  /** Solo superAdmin: vuelve a modo plataforma (sin tenant activo). */
  const deselectTenant = useCallback(
    async () => {
      if (!state.token) throw new Error("No hay sesión iniciada");
      setApiError(null);
      const client = new ApiClient({ baseUrl: baseUrl ?? "", getToken: () => state.token });
      const result = await client.post<{ accessToken: string }>(
        "/api/v1/auth/deselect-tenant",
        {},
      );
      setState((previous) => {
        const next = { ...previous, token: result.accessToken, activeTenantId: null, requiresTenantSelection: false };
        persistSession(next);
        return next;
      });
    },
    [baseUrl, state.token],
  );

  const logout = useCallback(() => {
    const empty: SessionState = { token: null, tenants: [], activeTenantId: null, requiresTenantSelection: false };
    persistSession(empty);
    setState(empty);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      isAuthenticated: Boolean(state.token),
      needsTenant: Boolean(state.token) && state.activeTenantId === null,
      login,
      selectTenant,
      switchTenant,
      deselectTenant,
      logout,
      apiError,
      clearApiError: () => setApiError(null),
    }),
    [state, login, selectTenant, switchTenant, deselectTenant, logout, apiError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth se ha usado fuera de <AuthProvider>");
  return context;
}
